import { camelCase } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useHistory } from 'react-router';
import { DesignServices as DesignServicesIcon } from '@material-ui/icons';
import { StateNodeType } from '@sierralabs/workflow';

import { FetchError } from '../../../common/api/api';
import { formatDate } from '../../../common/formatter';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import { useGetUserTaskSummary } from '../../../user/User.hooks';
import { getProductEntityIdPrefix } from '../../products/Product.config';
import { useGetPendingProductItems } from '../../products/Product.hooks';
import {
  ProductEntity,
  ProductTaskTypeEntities
} from '../../products/Product.interface';
import { parseTaskStatus } from '../../tasks/task.helper';
import { getTaskPath } from '../../tasks/Task.hooks';
import { TaskSummaryItem } from '../../tasks/Task.interface';
import { WorkflowEntity } from '../../workflow/Workflow.interface';
import { DashboardCardProps, DashboardItem } from '../Dashboard.interface';
import BaseDashboardCard, { DashboardCardOrder } from './BaseDashboardCard';

const orderMap: { [key in DashboardCardOrder]: string } = {
  title: 'task.modified',
  status: 'task.status'
};

const DashboardProductCard: React.FC<DashboardCardProps> = (
  props: DashboardCardProps
) => {
  const { search, onError } = props;
  const limit: number = 10;
  const history = useHistory();
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const [page, setPage] = useState<number>(0);
  const [order, setOrder] = useState(orderMap.title);
  const [orderDirection, setOrderDirection] = useState<'ASC' | 'DESC'>('DESC');
  const [entities, setEntities] = useState<ProductEntity[]>([]);
  // disable clicking items while the selected item is loading for redirect
  const [loading, setLoading] = useState(false);
  const {
    data: summary,
    isLoading: isLoadingSummary,
    error: taskSummaryError,
    get: getTaskSummary
  } = useGetUserTaskSummary();
  const {
    data,
    isLoading: isLoadingItems,
    error: itemsError,
    get
  } = useGetPendingProductItems();
  const isLoading: boolean = isLoadingSummary || isLoadingItems || loading;
  const error: FetchError | undefined = taskSummaryError || itemsError;

  const items: DashboardItem[] = useMemo(() => {
    return entities.map((item: ProductEntity): DashboardItem => {
      const { task } = item;
      const { parent } = task || {};
      const type: WorkflowEntity | undefined = parent?.type || task.type;
      let prefix;
      switch (type) {
        case WorkflowEntity.DesignVerification:
          prefix = getProductEntityIdPrefix('verification');
          break;
        case WorkflowEntity.DesignValidation:
          prefix = getProductEntityIdPrefix('validation');
          break;
        default:
          prefix = getProductEntityIdPrefix(camelCase(type) as any);
          break;
      }
      const entityId = `${prefix}-${item.id}`;
      const primary = `${entityId} / ${item.title}`;
      const taskType = task.parent?.taskType || task.taskType;
      const tooltip = primary + (taskType?.label ? ` / ${taskType.label}` : '');
      return {
        id: entityId,
        disabled: loading,
        onClick: async () => {
          setLoading(true);
          // prioritize parent task since the task can be a subTask
          const targetTask = task.parent || task;
          const result = await getTaskPath({
            taskId: targetTask.id || 0,
            taskEntity: item,
            targetEntity: targetTask.taskType?.targetEntity
          });
          setLoading(false);

          if (!result) {
            // do nothing
          } else if (result.path) {
            history.push(result.path);
          } else if (result.error) {
            onErrorRef.current?.(result.error);
          }
        },
        columns: [
          { tooltip, primary, secondary: formatDate(item.modified, true) },
          { render: () => <TaskStatusLabel task={task} /> }
        ]
      };
    });
  }, [entities, history, loading]);

  const sortEntities = useCallback(
    (entities: ProductEntity[]): ProductEntity[] => {
      return Array.from(entities).sort((a: ProductEntity, b: ProductEntity) => {
        if (orderDirection === 'DESC') {
          const temp: ProductEntity = a;
          a = b;
          b = temp;
        }

        switch (order) {
          case 'task.modified':
            return +a.modified - +b.modified;
          case 'task.status': {
            const aStatus = parseTaskStatus(a.task.status || '');
            const bStatus = parseTaskStatus(b.task.status || '');
            return aStatus.localeCompare(bStatus);
          }
        }

        // fallback to product entity title
        return (a.title || '').localeCompare(b.title || '');
      });
    },
    [order, orderDirection]
  );

  // Get pending Items total
  const itemTotal: number = useMemo(() => {
    return (summary || []).reduce((total: number, item: TaskSummaryItem) => {
      return total + item.total;
    }, 0);
  }, [summary]);

  useEffect(() => {
    getTaskSummary({
      entities: ProductTaskTypeEntities,
      excludeTypes: [StateNodeType.Final]
    });
  }, [getTaskSummary]);

  useEffect(() => {
    get({ page, limit, search: search || '' });
  }, [get, page, limit, search]);

  useEffect(() => {
    if (data) {
      const newItems: ProductEntity[] = data.data
        .filter((newItem: ProductEntity) => {
          return !entities.find(
            (item: ProductEntity) => item.id === newItem.id
          );
        })
        .concat(entities);
      setEntities(sortEntities(newItems));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, sortEntities]);

  useEffect(() => {
    if (error) {
      onErrorRef.current?.(error);
    }
  }, [error]);

  return (
    <BaseDashboardCard
      useItemGraph
      items={items}
      itemList={summary}
      total={itemTotal}
      isLoading={isLoading}
      icon={<DesignServicesIcon />}
      title='Pending Product Items'
      emptyState='No pending Product items.'
      page={page}
      limit={limit}
      orderDirection={orderDirection}
      setPage={setPage}
      setOrderDirection={setOrderDirection}
      isActive={(o: DashboardCardOrder) => orderMap[o] === order}
      setOrder={(order: DashboardCardOrder) => setOrder(orderMap[order])}
    />
  );
};

export default DashboardProductCard;
